/**
 * @fileoverview Hook exposing the on-demand legacy department list used by the
 *   onboarding wizard's "existing client" auto-fill.
 * @module system-web/hooks/useLegacyDepartments
 *
 * Responsibilities:
 * - Own fetching/error/result state for `fetchLegacyDepartments`.
 * - Reset results when the selected legacy database changes.
 *
 * Dependencies:
 * - ../services/legacyMigrationService (fetchLegacyDepartments).
 *
 * Architecture:
 * - Online exception (Streamline SQL data); no Dexie table, no liveQuery.
 * - Components never call apiClient — all network I/O goes through the service.
 *
 * Notes:
 * - Fetch is explicit (button-triggered), not automatic on mount.
 *
 * @hook useLegacyDepartments
 *
 * Purpose:
 * - Provide legacy departments for the selected database plus fetch controls.
 *
 * Returns:
 * - departments, physicalDatabase, fetching, error, fetchDepartments()
 *
 * Side Effects:
 * - Clears departments/physicalDatabase/error when `database` changes.
 *
 * Offline Behavior:
 * - fetchDepartments rejects offline; error is surfaced for retry.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchLegacyDepartments, type LegacyDepartment } from '../services/legacyMigrationService';

export interface UseLegacyDepartmentsResult {
  departments: LegacyDepartment[];
  /** Physical Streamline DB the server actually queried (confirmation badge). */
  physicalDatabase: string | null;
  fetching: boolean;
  error: string | null;
  fetchDepartments: () => Promise<void>;
}

export function useLegacyDepartments(database: string | undefined): UseLegacyDepartmentsResult {
  const [departments, setDepartments] = useState<LegacyDepartment[]>([]);
  const [physicalDatabase, setPhysicalDatabase] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    requestSeq.current += 1;
    setDepartments([]);
    setPhysicalDatabase(null);
    setFetching(false);
    setError(null);
  }, [database]);

  const fetchDepartments = useCallback(async () => {
    if (!database) return;
    const seq = ++requestSeq.current;
    setFetching(true);
    setError(null);
    setPhysicalDatabase(null);
    try {
      const result = await fetchLegacyDepartments(database);
      if (seq !== requestSeq.current) return;
      setDepartments(result.departments);
      setPhysicalDatabase(result.physicalDatabase);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setError(err?.message || 'Failed to fetch departments');
      setDepartments([]);
    } finally {
      if (seq === requestSeq.current) setFetching(false);
    }
  }, [database]);

  return { departments, physicalDatabase, fetching, error, fetchDepartments };
}
